"use client";

import { Box, SxProps, Theme, Typography } from "@mui/material";
import React, { useState } from "react";
import Image from "next/image";
import DateIcon from "@mui/icons-material/TodayRounded";
import LocationIcon from "@mui/icons-material/FmdGoodOutlined";
import { convertToDateV1 } from "@/helpers/helpers";

import LinkButton from "../common/LinkButton";
import { FlyCruise } from "@/interfaces/ICruiseDeals";

const PromoCruiseCard: React.FC<{
  cruise: FlyCruise;
  href?: string;
  sx?: SxProps<Theme>;
}> = ({ cruise, href, sx }) => {
  const [readMore, setReadMore] = useState(false);
  return (
    <Box
      sx={{
        bgcolor: "#f2f2f2",
        width: "100%",
        borderRadius: "15px",
        overflow: "hidden",
        transition: "all 0.5s",
        p: "8px",
        height: "fit-content",
        position: "relative",
        ...sx,
      }}
    >
      {cruise?.shipImageHref && (
        <Box
          sx={{
            position: "relative",
            height: "200px",
            width: "100%",
          }}
        >
          <Image
            src={cruise?.shipImageHref}
            alt={cruise?.shipTitle ?? ""}
            fill
            unoptimized
            loader={({ src }) => src}
            style={{ objectFit: "cover", objectPosition: "center", borderRadius: "14px" }}
          />
          <Box
            sx={{
              position: "absolute",
              top: "8px",
              right: "8px",
              width: "fit-content",
              height: "fit-content",
              img: {
                width: "100%",
                height: "50px",
                p: "8px",
                bgcolor: "#fff",
                boxShadow: "0px 0px 15px #C1BEFFaa",
                borderRadius: "10px",
                objectFit: "contain",
                objectPosition: "center",
              },
            }}
          >
            <Image
              src={cruise?.operatorImageHref ?? ""}
              alt={cruise?.operatorTitle ?? ""}
              width={100}
              height={50}
              unoptimized
              loader={({ src }) => src}
            />
          </Box>
        </Box>
      )}

      <Box>
        <Box
          sx={{
            mt: "8px",
            pl: "2px",
            mb: "1rem",
          }}
        >
          <Box
            sx={{
              position: "relative",
              overflow: { xs: "hidden" },
              WebkitLineClamp: 1,
              display: readMore ? "block" : "-webkit-box",
              WebkitBoxOrient: "vertical",
              textOverflow: "ellipsis",
              mb: "6px",
              cursor: "pointer",
            }}
            onClick={() => setReadMore((prev) => !prev)}
          >
            <Typography variant="h5" sx={{ transition: "0.5s", fontSize: "16px" }}>
              {cruise?.name}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            }}
          >
            <Box>
              <Box
                sx={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#7D46C9",
                  fontFamily: "'blauerNue', 'blauerNue Fallback'",
                  mb: "4px",
                }}
              >
                Embarking on
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  fontSize: "14px",
                  gap: "3px",
                  color: "#444",
                  ml: "-4px",
                  mb: "2px",
                  svg: {
                    width: "20px",
                    height: "20px",
                    color: "#555",
                  },
                }}
              >
                <LocationIcon />
                {cruise?.startsAt}
              </Box>
            </Box>

            <Box>
              <Box
                sx={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#7D46C9",
                  fontFamily: "'blauerNue', 'blauerNue Fallback'",
                  mb: "4px",
                }}
              >
                Embarking From
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  fontSize: "14px",
                  gap: "3px",
                  color: "#444",
                  ml: "-4px",
                  mb: "2px",
                  svg: {
                    width: "20px",
                    height: "20px",
                    color: "#555",
                  },
                }}
              >
                <DateIcon />
                {convertToDateV1(cruise?.startsOn ?? "")}
              </Box>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
            position: "relative",
            height: "100%",
            width: "100%",
            pl: "2px",
            bgcolor: "#f2f2f2",
          }}
        >
          <Box sx={{ position: "relative" }}>
            <Box
              sx={{
                fontSize: "12px",
                fontWeight: 700,
                fontFamily: "'blauerNue', 'blauerNue Fallback'",
                mt: "8px",
                lineHeight: 1.2,
              }}
            >
              PRICE FROM
            </Box>
            <Typography
              variant="body2"
              sx={{
                mt: "-3px",
                color: "#E30053",
                fontSize: "25px",
                lineHeight: 1.1,
                span: {
                  color: "#A8215C",
                  fontSize: "22px",
                },
                ".gbp": {
                  fontWeight: 900,
                  mr: "2px",
                },
                ".pp": {
                  fontWeight: 400,
                  ml: "2px",
                },
              }}
            >
              <span className="gbp">£</span>
              {cruise?.lowestPriceInfo?.price}
              <span className="pp">pp</span>

              {cruise?.lowestPriceInfo && (
                <Box
                  sx={{
                    fontSize: "12px",
                    fontWeight: 400,
                    position: "absolute",
                    top: "8px",
                    left: "78px",
                    width: "max-content",
                    color: "#060273",
                  }}
                >
                  {cruise?.lowestPriceInfo?.displayName}
                </Box>
              )}
            </Typography>
          </Box>

          <LinkButton href={href!}>View Cruise</LinkButton>
        </Box>
      </Box>
    </Box>
  );
};

export default PromoCruiseCard;
